import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Radar, BookOpen, TrendingUp, Clock, CheckCircle2, Crown, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import Disclaimer from "@/components/Disclaimer";
import { SignalAccuracy } from "@/components/SignalAccuracy";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [sub, setSub] = useState(null);
  const [payCfg, setPayCfg] = useState(null);

  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
    api.get("/subscription/my").then(({ data }) => setSub(data)).catch(() => {});
    api.get("/payments/config").then(({ data }) => setPayCfg(data)).catch(() => {});
  }, []);

  const loadRzp = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const sc = document.createElement("script");
    sc.src = "https://checkout.razorpay.com/v1/checkout.js";
    sc.onload = () => resolve(true);
    sc.onerror = () => resolve(false);
    document.body.appendChild(sc);
  });

  useEffect(() => {
    if (user && !user.access) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const access = user?.access;
  const subscribe = async (planId) => {
    if (payCfg?.enabled) {
      try {
        const ok = await loadRzp();
        if (!ok) { toast.error("Failed to load payment gateway"); return; }
        const { data } = await api.post("/payments/create-order", { plan_id: planId });
        const rzp = new window.Razorpay({
          key: data.key_id, amount: data.amount, currency: data.currency,
          name: "AI Trading Copilot", description: `${data.plan_name} subscription`, order_id: data.order_id,
          prefill: { name: data.name, email: data.email }, theme: { color: "#10B981" },
          handler: async (resp) => {
            try {
              await api.post("/payments/verify", {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success("Payment successful — subscription active!");
              await refresh();
              const { data: my } = await api.get("/subscription/my");
              setSub(my);
            } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Verification failed"); }
          },
        });
        rzp.on("payment.failed", () => toast.error("Payment failed — please try again"));
        rzp.open();
      } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not start checkout"); }
      return;
    }
    try {
      await api.post("/subscription/subscribe", { plan_id: planId });
      toast.success("Subscription request submitted. Admin will confirm shortly.");
      const { data } = await api.get("/subscription/my");
      setSub(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="text-slate-400 mt-1">Your trading command center.</p>
        </div>

        {/* Access status card */}
        <div className="glass rounded-2xl p-6" data-testid="access-card">
          {access?.reason === "trial" && (
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center"><Clock className="w-6 h-6 text-amber-400" /></div>
                <div>
                  <p className="font-semibold text-lg">Free Trial Active</p>
                  <p className="text-slate-400 text-sm"><span className="font-mono-num text-amber-400 font-bold">{access.days_left}</span> days remaining · Full access to all features</p>
                </div>
              </div>
              <a href="#plans" className="text-emerald-400 text-sm font-semibold hover:underline">Upgrade →</a>
            </div>
          )}
          {access?.reason === "subscription" && (
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center"><Crown className="w-6 h-6 text-emerald-400" /></div>
              <div>
                <p className="font-semibold text-lg">Active Subscription</p>
                <p className="text-slate-400 text-sm">Plan: {access.plan} · Renews {new Date(access.expires_at).toLocaleDateString()}</p>
              </div>
            </div>
          )}
          {access?.reason === "admin" && (
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/15 flex items-center justify-center"><Crown className="w-6 h-6 text-cyan-400" /></div>
              <div><p className="font-semibold text-lg">Administrator Access</p><p className="text-slate-400 text-sm">Unlimited platform access</p></div>
            </div>
          )}
          {access?.reason === "expired" && (
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-red-500/15 flex items-center justify-center"><Clock className="w-6 h-6 text-red-400" /></div>
                <div><p className="font-semibold text-lg">Trial Ended</p><p className="text-slate-400 text-sm">Subscribe to continue using the AI Copilot.</p></div>
              </div>
              <a href="#plans" className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold px-4 py-2 rounded-full text-sm">View Plans</a>
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            { to: "/copilot", icon: Radar, title: "AI Copilot", desc: "Analyze charts live", color: "emerald" },
            { to: "/journal", icon: BookOpen, title: "Trade Journal", desc: "Log your trades", color: "cyan" },
            { to: "/analytics", icon: TrendingUp, title: "Analytics", desc: "Track performance", color: "amber" },
          ].map((c) => (
            <Link key={c.to} to={c.to} data-testid={`quick-${c.title.toLowerCase().replace(/\s/g, "-")}`}
              className="glass rounded-xl p-6 hover:border-emerald-500/30 transition-colors group">
              <c.icon className={`w-7 h-7 mb-3 text-${c.color}-400`} />
              <h3 className="font-semibold text-lg flex items-center gap-2">{c.title} <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" /></h3>
              <p className="text-slate-400 text-sm">{c.desc}</p>
            </Link>
          ))}
        </div>

        {/* Live signal accuracy */}
        <SignalAccuracy />

        {/* Plans */}
        {user?.role !== "admin" && (
          <div id="plans">
            <h2 className="font-display text-2xl font-bold mb-4">Subscription Plans</h2>
            {sub?.transactions?.some((t) => t.status === "pending") && (
              <div className="mb-4 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3" data-testid="pending-payment-notice">
                You have a pending subscription request awaiting admin confirmation.
              </div>
            )}
            <div className="grid md:grid-cols-3 gap-5">
              {(config?.plans || []).map((p, i) => (
                <div key={p.id} className={`glass rounded-2xl p-6 ${i === 1 ? "border-emerald-500/40" : ""}`} data-testid={`dash-plan-${p.id}`}>
                  <h3 className="font-sub font-bold text-xl mb-1">{p.name}</h3>
                  <div className="flex items-baseline gap-1 mb-4"><span className="font-mono-num text-3xl font-extrabold">${p.price}</span><span className="text-slate-500 text-sm">/{p.interval}</span></div>
                  <ul className="space-y-1.5 mb-5">{p.features.map((f, j) => (<li key={j} className="flex gap-2 text-sm text-slate-300"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{f}</li>))}</ul>
                  <Button onClick={() => subscribe(p.id)} data-testid={`subscribe-${p.id}`} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold">Subscribe</Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <Disclaimer text={config?.disclaimer} compact />
      </div>
    </Layout>
  );
}
